import { action, flow, register, types, volatile } from "@gadgetinc/mobx-quick-tree";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { when } from "mobx";
import { createRef, RefObject } from "react";
// eslint-disable-next-line workspaces/require-dependency
import {
  AppClassification,
  AppClassificationType,
  AppClassificationTypes,
  appClassificationTypeToAppTemplateType,
  appDisplayTypeForSummon,
  AuthClassificationType,
  AuthClassificationTypes,
  authClassificationTypeToShopifyAppType,
  authClassificationTypeToWebAppAuthType,
  GadgetAppSummon,
  MAX_SUMMON_PROMPT_LENGTH,
} from "state-trees/src/assistant/summon";
import { SummonAppLoadingMessage, SummonCopy, SummonSetupMessage } from "state-trees/src/summonMetadata/SummonCopy";
import { GadgetDomainModelClass, Thunk, unthunk } from "state-trees/src/utils";
import { GadgetAuthConfig } from "../../components/auth/AuthLayout";
import { normalizeSubdomainName, sanitizeSubdomainInput } from "./NewAppForm";
import { Survey } from "./Survey";

const SetupSteps: {
  message: Thunk<string, [{ devDomain: string; prodDomain: string }]>;
}[] = [
  {
    message: SummonCopy.Setup.hosting,
  },
  {
    message: ({ devDomain }) => `${SummonCopy.Setup.devUrl} ${devDomain}`,
  },
  {
    message: ({ prodDomain }) => `${SummonCopy.Setup.prodUrl} ${prodDomain}`,
  },
  {
    message: SummonCopy.Setup.database,
  },
  {
    message: SummonCopy.Setup.backend,
  },
  {
    message: SummonCopy.Setup.frontend,
  },
  {
    message: SummonCopy.Setup.dependencies,
  },
];

const BigCommerceNotYetSupported =
  "I'm sorry, but I don't currently build BigCommerce apps, but I plan to be able to soon. You can still build BigCommerce apps with Gadget, but you'll need to do without my assistance for now. Let me take you to the right place to get started.";

const UnsupportedAppTypeMessage = "I'm sorry, but I don't currently build apps of this type. Let's try again with a different app type.";

const UnknownAppTypeMessage = "I'm sorry but I didn't understand your request. Let's try again.";

/** Represents a session for summoning an app using our AI assistant */
@register
export class AppSummonSession extends GadgetDomainModelClass("AppSummonSession", {
  prompt: types.maybe(types.string),
  internalName: types.maybe(types.string),
  startedAt: types.maybeNull(types.number),
  loading: types.optional(types.boolean, false),
  validating: types.optional(types.boolean, false),
  appType: types.maybeNull(types.enumeration(Object.values(AppClassificationTypes))),
  authType: types.maybeNull(types.enumeration(Object.values(AuthClassificationTypes))),
  hideAppType: types.optional(types.boolean, false),
  hideAuthType: types.optional(types.boolean, false),
  summary: types.maybeNull(types.string),
  appSummaryState: types.optional(types.enumeration(Object.values(["loading", "editing", "completed"])), "loading"),
  errorMessage: types.maybe(types.string),
  invalidNameError: types.maybe(types.string),
  cfTurnstileToken: types.maybe(types.string),
  cfTurnstileRequiresInteraction: types.optional(types.boolean, false),
  systemMessage: types.maybeNull(types.string),
  settingUpMessage: types.maybeNull(types.string),
  settingUpSteps: types.optional(types.array(types.frozen<{ message: string; completed: boolean }>()), []),
  survey: types.maybe(Survey),
  fastMode: types.optional(types.boolean, false),
}) {
  @volatile(() => createRef<TurnstileInstance | undefined>())
  cfTurnstileRef!: RefObject<TurnstileInstance | undefined>;

  get showTurnstile() {
    return this.cfTurnstileRequiresInteraction;
  }

  get promptErrors() {
    const errors: string[] = [];
    if (!this.prompt) {
      errors.push("Prompt is required");
    }
    if (this.prompt && this.prompt.length > MAX_SUMMON_PROMPT_LENGTH) {
      errors.push(`Prompt is too long.`);
    }
    return errors;
  }

  get validPrompt() {
    return this.promptErrors.length === 0;
  }
  get started() {
    return this.startedAt !== null;
  }

  get canSubmit() {
    return this.validPrompt && !this.started;
  }

  get errored() {
    return !!this.errorMessage;
  }

  get classified() {
    return this.appType && this.authType;
  }

  get appTemplateType() {
    return this.appType ? appClassificationTypeToAppTemplateType(this.appType, this.authType) : null;
  }

  get shopifyAppType() {
    return this.authType ? authClassificationTypeToShopifyAppType(this.authType) : null;
  }

  get webAppAuthType() {
    return this.authType ? authClassificationTypeToWebAppAuthType(this.authType) : null;
  }

  @action
  reset() {
    this.prompt = undefined;
    this.internalName = undefined;
    this.appType = null;
    this.authType = null;
    this.summary = null;
    this.loading = false;
    this.validating = false;
    this.startedAt = null;
    this.errorMessage = undefined;
    this.invalidNameError = undefined;
    this.appSummaryState = "loading";
    this.systemMessage = null;
    this.settingUpMessage = null;
    this.settingUpSteps.clear();
  }

  @action
  setFastMode(fastMode: boolean) {
    this.fastMode = fastMode;
  }

  @action
  setErrorMessage(errorMessage: string | undefined) {
    this.errorMessage = errorMessage;
  }

  @action
  validate = flow(function* (this: AppSummonSession) {
    this.invalidNameError = undefined;
    this.validating = true;

    try {
      const response = yield fetch("/auth/api/apps/validate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subdomain: this.appSlug }),
      });

      const json = (yield response.json()) as { errors?: { domain?: string } };

      if (json.errors?.domain) {
        this.invalidNameError = json.errors.domain;
        return false;
      }

      return true;
    } catch (error: any) {
      this.errorMessage = error.message ?? "An unknown error occurred";
      return false;
    } finally {
      this.validating = false;
    }
  });

  @action
  setPrompt(prompt: string) {
    this.prompt = prompt;
  }

  get appSlug() {
    return this.internalName ? normalizeSubdomainName(this.internalName) : undefined;
  }

  @action
  setAppSlug(appSlug: string) {
    this.internalName = sanitizeSubdomainInput(appSlug);
  }

  @action
  setAppType(appType: AppClassificationType) {
    this.appType = appType;
  }

  @action
  setAuthType(authType: AuthClassificationType) {
    this.authType = authType;
  }

  @action
  setSummary(summary: string) {
    this.summary = summary;
  }

  @action
  clearErrorMessage() {
    this.errorMessage = undefined;
  }

  @action
  setSystemMessage(systemMessage: string) {
    this.systemMessage = systemMessage;
  }

  @action
  setSettingUpMessage(settingUpMessage: string) {
    this.settingUpMessage = settingUpMessage;
  }

  @action
  setCfTurnstileToken(token: string) {
    this.cfTurnstileToken = token;
  }

  @action
  setCfTurnstileRequiresInteraction(requiresInteraction: boolean) {
    this.cfTurnstileRequiresInteraction = requiresInteraction;
  }

  @action
  setAppSummaryState(state: "loading" | "editing" | "completed") {
    this.appSummaryState = state;
  }

  @action
  resetStartedAt() {
    this.startedAt = null;
  }

  @action
  continueOnDesktop = flow(function* (this: AppSummonSession) {
    yield fetch("/auth/api/summon/continue-on-desktop", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        summonKey: this.key,
        prompt: this.prompt,
        appName: this.appSlug ?? "",
        summary: this.summary ?? "",
        appType: this.appType,
        authType: this.authType,
      }),
    });
  });

  @action
  classify = flow(function* (this: AppSummonSession) {
    this.errorMessage = undefined;
    this.loading = true;

    try {
      const response = yield fetch("/auth/api/summon/classify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt: this.prompt, appType: this.appType ?? undefined, authType: this.authType ?? undefined }),
      });

      if (!response.ok) {
        this.errorMessage = "An unknown error occurred";
        return;
      }

      const json = (yield response.json()) as AppClassification;

      console.debug("[gadget-summon-session] classified app", json);

      if (this.prompt?.includes("fast mode")) {
        this.fastMode = true;
      }

      if (["Shopify", "Web"].includes(json.appType)) {
        this.setAppType(json.appType);
        this.setAuthType(json.authType);
        this.setSummary(json.summary);
        this.setAppSlug(json.slug);

        return true;
      } else if (json.appType === "BigCommerce") {
        this.startedAt = Date.now();

        for (const char of BigCommerceNotYetSupported) {
          this.setSystemMessage(this.systemMessage ? this.systemMessage + char : char);
          yield new Promise<void>((resolve) => setTimeout(() => resolve(), 10));
        }

        yield new Promise<void>((resolve) => setTimeout(() => resolve(), 4000));

        window.location.href = "/auth/team/create-app?type=BigCommerceApp";

        return false;
      } else {
        const message = json.appType === "Unsupported" ? UnsupportedAppTypeMessage : UnknownAppTypeMessage;

        this.errorMessage = undefined;
        for (const char of message) {
          this.setErrorMessage(this.errorMessage ? this.errorMessage + char : char);
          yield new Promise<void>((resolve) => setTimeout(() => resolve(), 10));
        }

        this.resetStartedAt();
        return false;
      }
    } catch (error: any) {
      console.error("[gadget-summon-session] Error in classify", error);
      this.reset();
      this.errorMessage = error.message ?? "An unknown error occurred";
    } finally {
      this.loading = false;
    }
  });

  /** Start the process on the backend and stream messages back to the client */
  @action
  summon = flow(function* (this: AppSummonSession, userEmailHash: string, config: GadgetAuthConfig) {
    if (this.loading) {
      return;
    }

    this.startedAt = Date.now();

    const streamRate = this.fastMode ? 0.01 : 10;

    this.loading = true;

    try {
      this.setAppSummaryState("editing");

      if (this.fastMode) {
        this.setAppSummaryState("completed");
      }

      yield when(() => this.appSummaryState === "completed");

      const devDomain = `https://${this.appSlug}--development.${config.defaultRenderDomain}`;
      const prodDomain = `https://${this.appSlug}.${config.defaultRenderDomain}`;

      const settingUpSteps = SetupSteps.map((step) => unthunk(step.message, { devDomain, prodDomain }));

      const appSummon: GadgetAppSummon = {
        summonKey: this.key,
        prompt: this.prompt ?? "",
        settingUpMessage: SummonSetupMessage,
        settingUpSteps,
        summary: this.summary ?? "",
        appName: this.appSlug!,
        userEmailHash,
        appTemplateType: this.appTemplateType!,
        shopifyAppType: this.shopifyAppType ?? undefined,
        appDisplayType: appDisplayTypeForSummon(this.appTemplateType!, this.shopifyAppType),
        loadingMessage: SummonAppLoadingMessage,
      };

      const redirectPromise = createAppAndPrefetchEditor(appSummon);

      let redirectUrl: string | null = null;

      for (const char of SummonSetupMessage) {
        this.setSettingUpMessage(this.settingUpMessage ? this.settingUpMessage + char : char);
        yield new Promise<void>((resolve) => setTimeout(() => resolve(), streamRate));
      }

      let idx = 0;
      for (const step of settingUpSteps) {
        const isLastStep = idx === settingUpSteps.length - 1;

        this.settingUpSteps.push({ message: step, completed: false });
        yield new Promise((resolve) => setTimeout(resolve, this.fastMode ? 1 : Math.floor(Math.random() * 1001) + 1000));

        if (isLastStep) {
          redirectUrl = yield redirectPromise;
        }
        this.settingUpSteps.splice(this.settingUpSteps.length - 1, 1, { message: step, completed: true });
        idx++;
      }

      window.location.href = redirectUrl!;
    } catch (error: any) {
      console.error("[gadget-summon-session] Error in summon", error);
      this.reset();
      this.errorMessage = error.message ?? "An unknown error occurred";
    } finally {
      this.loading = false;
    }
  });
}

const createAppAndPrefetchEditor = async (appSummon: GadgetAppSummon) => {
  const appCreationResponse = await fetch("/auth/api/summon/summon", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ appSummon }),
  });

  if (!appCreationResponse.ok) {
    throw new Error("An unknown error occurred while creating your app; please try again later.");
  }

  const json = (await appCreationResponse.json()) as { editURL: string };

  return json.editURL;
};
